import { useNavStore } from "@/shared/store/useNavStore";
import { isBrowser } from "@/shared/utils/dom";
import { Typography } from "@graphy/ui";
import dynamic from "next/dynamic";
import { useEffect, useLayoutEffect, useRef, useState } from "react";

// lottie-web (pulled in by lottie-react) touches `document` at module load,
// crashing during SSR. Load client-side only.
const Lottie = dynamic(() => import("lottie-react"), { ssr: false }) as any;
import HeroStats, { HeroStatsPropsType } from "../atoms/HeroStats";
import { CaptionWithImg } from "../molecules/CaptionWithImg";
import CtaButtonGroup, { CtaType } from "../molecules/CtaButtonGroup";

export type HomeHeroSectionPropsType = {
  sectionNames: string[];
  title: string;
  description: string;
  caption: string;
  homeHeroStatsProps: HeroStatsPropsType;
  lottie: any;
  mWebLottie: any;
  ctas: CtaType[];
};

const Hero = ({
  sectionNames,
  title,
  description,
  lottie,
  mWebLottie,
  homeHeroStatsProps,
  caption,
  ctas,
}: HomeHeroSectionPropsType) => {
  const lottieRef = useRef(null);
  const lottieRefMweb = useRef(null);
  const [productVal, setProductVal] = useState(sectionNames[0]);
  const [activeId, setActiveId] = useState(0);
  const [rotatingIdx, setRotatingIdx] = useState(0);
  const [fade, setFade] = useState(true);
  const fadeTimeout = useRef<NodeJS.Timeout | null>(null);

  const { setShowSignUp } = useNavStore();
  const colorClasses = [
    "text-purple-3",
    "text-green-3",
    "text-red-0",
    "text-yellow-3",
    "text-pink-0",
  ];

  const [textClasses, setTextClasses] = useState<
    "opacity-0" | "transition-opacity duration-700"
  >("opacity-0");

  useEffect(() => {
    window.addEventListener("scroll", () => {
      if (!scrollY) {
        setShowSignUp(true);
      } else {
        setShowSignUp(false);
      }
    });
    return () => {
      setShowSignUp(true);
    };
  }, []);

  useEffect(() => {
    setTextClasses("opacity-0");

    const timeoutId = setTimeout(() => {
      setTextClasses("transition-opacity duration-700");
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [productVal]);

  useEffect(() => {
    const interval = setInterval(() => {
      setFade(false); 
      fadeTimeout.current = setTimeout(() => {
        setRotatingIdx((prev) => (prev + 1) % sectionNames.length);
        setFade(true); 
      }, 200); 
    }, 2000);
    return () => {
      clearInterval(interval);
      if (fadeTimeout.current) clearTimeout(fadeTimeout.current);
    };
  }, []);

  // const topPosition = heroScrollRef?.current?.getBoundingClientRect().top;
  const onScroll = () => {
    const heroContainer = document.getElementById("hero-container");
    let totalHeight = heroContainer?.clientHeight || 0 - 200;

    let scrollFromTop = window.scrollY;
    let totalFrames = 300;
    let scrollPercentage = (scrollFromTop * 100) / (totalHeight || 1);
    let scrollPercentRounded = Math.floor(scrollPercentage);

    const sectionCount = sectionNames.length;
    const percentPerSection = 100 / sectionCount;
    let scrollIndex = Math.floor(scrollPercentRounded / percentPerSection);
    if (scrollIndex >= sectionCount) {
      scrollIndex = sectionCount - 1;
      setActiveId(scrollIndex);
    }
    if (productVal !== sectionNames[scrollIndex]) {
      setProductVal(sectionNames[scrollIndex]);
      setActiveId(scrollIndex);
    }
  };

  useLayoutEffect(() => {
    if (isBrowser) {
      window?.addEventListener("scroll", onScroll);
    }
    return () => window?.removeEventListener("scroll", onScroll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productVal]);

  return (
    <div>
      <div className="relative mt-0 md:mt-0 lg:mt-0 max-h-[437px]   w-full" id="hero-container">
        <div
          className="absolute hidden h-[80%] w-[70%] md:block xl:w-[75%] 3xl:w-[65%]"
          style={{
            borderRadius: "0 400px 400px 0"
          }}
        ></div>

        {/* 2xl:hero-max-width */}
        <div className="container relative w-full px-4 md:px-6 lg:px-8 xl:px-12 2xl:px-16 mx-auto" style={{maxHeight: '437px'}}>
          <div className="flex flex-col items-center md:flex-row md:justify-between w-full gap-6 md:gap-8 lg:gap-" style={{maxHeight: '437px'}}>
            <div className="w-full md:w-[60%] lg:w-[65%] xl:w-[65%] px-4 md:px-0">
              <div className="z-20 mx-auto">
                <h1 className="font-semibold leading-tight text-grey-4 text-[24px] md:text-[32px] lg:text-[36px] xl:text-[42px]">
                  {title.split(' Platform for')[0]}
                </h1>

                <div className="flex items-baseline whitespace-nowrap md:whitespace-normal lg:whitespace-normal">
                  <h1 className="font-semibold leading-tight text-grey-4 text-[24px] md:text-[32px] lg:text-[36px] xl:text-[42px]">
                    Platform for
                  </h1>
                  <h1
                    className={`ml-1 md:ml-2 font-semibold leading-tight text-[24px] md:text-[32px] lg:text-[36px] xl:text-[42px] transition-opacity duration-200 ${fade ? 'opacity-100' : 'opacity-0'} ${colorClasses[rotatingIdx]}`}
                    id="product-features"
                  >
                    {sectionNames[rotatingIdx]}
                  </h1>
                </div>
                {/* <Image
                  src="/images/home-hero-curl-arrow.svg"
                  width={56}
                  height={80}
                  alt="graphy"
                  className="ml-16 hidden translate-y-2 rotate-[8deg] md:hidden"
                /> */}
              </div>
              <Typography
                className="mb-4 mt-3 max-w-md !text-body md:my-5 md:!text-title6"
                fontWeight="font-light"
                colorClass="text-grey-4"
              >
                {description}
              </Typography>
              <div>
              {ctas && <CtaButtonGroup ctas={ctas} gap="gap-3  sm:w-[10px] md:w-auto lg:w-[427px] max-w-[427px] flex gap-3 h-12 mb-3" />}
              </div>

              <CaptionWithImg
                key={caption}
                imgSrc={"/images/tiny-features/no-fee.svg"}
                caption={caption}
              />
              {/* <div className="bg-yellow mt-12 h-4 rounded-3xl"></div> */}
              <div className="mt-6 md:mt-8 ">
                {" "}
                <HeroStats {...homeHeroStatsProps} />
              </div>
            </div>

            <div className="mb-6 md:mt-0 md:hidden xl:min-w-0 flex justify-center items-center relative w-full h-[300px] sm:h-[316px] rounded-[10.42px] bg-white" style={{ pointerEvents: 'none' }}>
              <div className="absolute top-[-4px] left-0 w-full h-[80px] pointer-events-none z-[2]" style={{background: 'linear-gradient(180deg, #FFFFFF 6.85%, rgba(255, 255, 255, 0) 100%)'}} />
              <div className="flex justify-center items-center w-full h-[280px] z-[1]">
                <Lottie //mobile
                  lottieRef={lottieRef}
                  autoplay={true}
                  loop={true}
                  animationData={mWebLottie}
                  className="w-[285px] h-[670px] mx-auto"
                />
              </div>
              <div className="absolute bottom-0 left-0 w-full h-[80px] rounded-b-[16.42px] pointer-events-none z-[2]" style={{background: 'linear-gradient(0deg, #FFFFFF 6.85%, rgba(255, 255, 255, 0) 100%)'}} />
            </div>
            <div className="w-full md:w-[45%] lg:w-[450px] xl:w-[450px] mt-6 hidden md:mt-0 md:block xl:min-w-0 flex justify-center items-center relative ml-[-32px] h-[600px] rounded-[16.42px] -mt-[40px] overflow-hidden border border-grey-1" style={{border: 'none'}}>
              <div className="absolute top-0 left-0 w-full h-[80px] rounded-t-[16.42px] pointer-events-none z-[2]" style={{background: 'linear-gradient(180deg, #FFFFFF 62.85%, rgba(255, 255, 255, 0) 100%)'}} />
              <Lottie //desktop
                lottieRef={lottieRefMweb}
                autoplay={true}
                loop={true}
                animationData={lottie}
                className="w-full h-full rounded-[16.42px] z-[1] object-contain"
              />
              <div className="absolute bottom-0 left-0 w-full h-[80px] rounded-b-[16.42px] pointer-events-none z-[2]" style={{background: 'linear-gradient(0deg, #FFFFFF 62.85%, rgba(255, 255, 255, 0) 100%)'}} />
            </div>
          </div>

          {/* {productVal !== sectionNames[sectionNames.length - 1] && (
            <div
              className="mt-4 w-full translate-y-[25px] cursor-pointer md:mt-8"
              onClick={() => {
                const heroContainer = document.getElementById("hero-container");
                let totalHeight = heroContainer?.clientHeight || 0;
                window.scrollTo({
                  top: totalHeight * 0.2 * (activeId + 1) + 100,
                  behavior: "smooth",
                });
              }}
            >
              <ScrollDownArrow customStyle={{ margin: "auto" }} />
            </div>
          )} */}
        </div>

        <div className="h-[80vh]"></div>
      </div>
    </div>
  );
};

export default Hero;
